import { useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Initial LiveKit join must succeed within this window or we terminal-fail. */
export const LIVEKIT_INITIAL_CONNECT_ESCALATE_MS = 45_000;

export type LiveKitConnectWatchdogInput = {
  escalateMs?: number;
  nowMs?: number;
};

export type LiveKitConnectWatchdogResult = {
  /** True while the room has never reached Connected and is still trying. */
  isInitialConnecting: boolean;
  /** Terminal failure: never connected within SLA or LiveKit onError. */
  connectFailed: boolean;
};

type LiveKitConnectFailureContextValue = {
  markConnectFailed: () => void;
  clearConnectFailed: () => void;
};

const LiveKitConnectFailureContext = createContext<LiveKitConnectFailureContextValue>({
  markConnectFailed: () => {},
  clearConnectFailed: () => {},
});

const LiveKitConnectErrorFlagContext = createContext(false);

/** Call from LiveKitRoom onError to model the real terminal failure mechanism. */
export function useLiveKitConnectFailureSignal(): LiveKitConnectFailureContextValue {
  return useContext(LiveKitConnectFailureContext);
}

function LiveKitConnectFailureState({
  connectFailedByError,
  children,
}: {
  connectFailedByError: boolean;
  children: ReactNode;
}) {
  return (
    <LiveKitConnectErrorFlagContext.Provider value={connectFailedByError}>
      {children}
    </LiveKitConnectErrorFlagContext.Provider>
  );
}

export function LiveKitConnectFailureProvider({ children }: { children: ReactNode }) {
  const [connectFailedByError, setConnectFailedByError] = useState(false);
  const markConnectFailed = useCallback(() => {
    setConnectFailedByError(true);
  }, []);
  const clearConnectFailed = useCallback(() => {
    setConnectFailedByError(false);
  }, []);
  const failureContextValue = useMemo(
    () => ({ markConnectFailed, clearConnectFailed }),
    [markConnectFailed, clearConnectFailed],
  );

  return (
    <LiveKitConnectFailureContext.Provider value={failureContextValue}>
      <LiveKitConnectFailureState connectFailedByError={connectFailedByError}>
        {children}
      </LiveKitConnectFailureState>
    </LiveKitConnectFailureContext.Provider>
  );
}

export function deriveLiveKitConnectWatchdog(input: {
  connectionState: ConnectionState;
  connectFailedByError: boolean;
  attemptSinceMs: number | null;
  nowMs: number;
  escalateMs?: number;
  wasEverConnected: boolean;
}): LiveKitConnectWatchdogResult {
  if (input.connectFailedByError) {
    return { isInitialConnecting: false, connectFailed: true };
  }

  if (input.wasEverConnected || input.connectionState === ConnectionState.Connected) {
    return { isInitialConnecting: false, connectFailed: false };
  }

  const isInitialAttempt = input.connectionState === ConnectionState.Connecting
    || input.connectionState === ConnectionState.Disconnected;

  if (!isInitialAttempt || input.attemptSinceMs == null) {
    return { isInitialConnecting: false, connectFailed: false };
  }

  const escalateMs = input.escalateMs ?? LIVEKIT_INITIAL_CONNECT_ESCALATE_MS;
  const elapsed = input.nowMs - input.attemptSinceMs;
  if (elapsed >= escalateMs) {
    return { isInitialConnecting: false, connectFailed: true };
  }

  return { isInitialConnecting: true, connectFailed: false };
}

/**
 * Detects LiveKit never-connected / onError and escalates to a terminal failure
 * instead of an infinite connecting spinner. Must render under LiveKit room context.
 */
export function useLiveKitConnectWatchdog(
  input: LiveKitConnectWatchdogInput = {},
): LiveKitConnectWatchdogResult {
  const connectionState = useConnectionState();
  const connectFailedByError = useContext(LiveKitConnectErrorFlagContext);
  const [attemptSinceMs, setAttemptSinceMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => input.nowMs ?? Date.now());
  const [wasEverConnected, setWasEverConnected] = useState(false);

  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      setWasEverConnected(true);
      setAttemptSinceMs(null);
      return;
    }
    if (wasEverConnected) return;
    setAttemptSinceMs((prev) => prev ?? Date.now());
  }, [connectionState, wasEverConnected]);

  useEffect(() => {
    if (attemptSinceMs == null || connectFailedByError) return undefined;
    const id = setInterval(() => {
      setNowMs(Date.now());
    }, 250);
    return () => clearInterval(id);
  }, [attemptSinceMs, connectFailedByError]);

  return deriveLiveKitConnectWatchdog({
    connectionState,
    connectFailedByError,
    attemptSinceMs,
    nowMs: input.nowMs ?? nowMs,
    escalateMs: input.escalateMs,
    wasEverConnected,
  });
}
